import React, { useCallback } from 'react';

import Button from 'app/components/flat/ui/Button';
import ModalDialog from 'app/components/flat/ui/ModalDialog';
import InputText from 'app/components/flat/ui/Inputs/InputText';
import SelectField from 'app/components/flat/ui/Inputs/SelectField';
import { BRAZILIAN_STATES } from 'app/components/OnBoardingModals/constants';
import colors from 'app/styles/theme/colors';
import { fontSizes } from 'app/styles/theme/sizes';
import {
  trackOabCaptureDismissed,
  OabCaptureDismissMethod,
  OabCaptureEntryStep,
  OabCaptureSurface,
} from 'app/services/tracking/oabCapture/oabCaptureEvents';

import illustration from '../modal-illustration.svg';
import { useOabRegistration } from './useOabRegistration';
import successIllustration from './success-illustration.svg';
import * as S from './styles';

const MAX_OAB_LENGTH = 8;
const TITLE_ID = 'oab-registration-title';

const { gray, green } = colors.designSystem;

type OabRegistrationModalProps = {
  surface: OabCaptureSurface;
  entryStep: OabCaptureEntryStep;
  onClose: () => void;
};

const OabRegistrationModal = ({
  surface,
  entryStep,
  onClose,
}: OabRegistrationModalProps): React.ReactElement => {
  const {
    oabNumber,
    uf,
    setOabNumber,
    setUf,
    isSubmitting,
    errorMessage,
    isRegistered,
    submit,
  } = useOabRegistration({ surface, entryStep });

  const postpone = useCallback(
    (method: OabCaptureDismissMethod): void => {
      trackOabCaptureDismissed({ surface, method, step: 'oab_form' });
      onClose();
    },
    [surface, onClose],
  );

  const close = useCallback(
    (method: OabCaptureDismissMethod): void => {
      if (isSubmitting) {
        return;
      }

      if (isRegistered) {
        onClose();
        return;
      }

      postpone(method);
    },
    [isSubmitting, isRegistered, onClose, postpone],
  );

  const selectedState =
    BRAZILIAN_STATES.find((state) => state.state === uf) ?? null;

  return (
    <ModalDialog
      open
      width="500px"
      aria-labelledby={TITLE_ID}
      onClose={(_event: unknown, reason: string) =>
        reason === 'escapeKeyDown' && close('esc')
      }
    >
      <S.Card>
        <S.Illustration
          src={isRegistered ? successIllustration : illustration}
          alt=""
        />
        <S.CloseButton
          type="button"
          aria-label="Fechar"
          disabled={isSubmitting}
          onClick={() => close('x')}
        >
          ×
        </S.CloseButton>

        <S.Body $align={isRegistered ? 'center' : 'left'}>
          {isRegistered ? (
            <>
              <S.Title id={TITLE_ID}>
                Já estamos buscando seus processos
              </S.Title>
              <S.Description>
                Pode deixar com a gente. Assim que a busca terminar, avisamos
                você por e-mail.
              </S.Description>
              <S.SuccessActions>
                <Button
                  type="button"
                  backgroundColor={green[500]}
                  hoverBackgroundColor={green[600]}
                  color={gray[0]}
                  width="100%"
                  height="40px"
                  borderRadius="8px"
                  fontSize={fontSizes.sm}
                  onClick={onClose}
                >
                  Entendi
                </Button>
              </S.SuccessActions>
            </>
          ) : (
            <>
              <S.Title id={TITLE_ID}>Vamos encontrar seus processos</S.Title>
              <S.Description>
                Assim que cadastrarmos sua OAB na Jusfy, você poderá monitorar
                todos os seus processos de forma gratuita
              </S.Description>

              <S.Form
                onSubmit={(event) => {
                  event.preventDefault();
                  submit();
                }}
              >
                <S.FieldsRow>
                  <S.NumberField>
                    <InputText
                      id="oab-registration-number"
                      label="Número da OAB"
                      required
                      inputHeight="40px"
                      inputMode="numeric"
                      maxLength={MAX_OAB_LENGTH}
                      value={oabNumber}
                      onChange={(event) => setOabNumber(event.target.value)}
                    />
                  </S.NumberField>

                  <S.StateField>
                    <SelectField
                      label="Estado"
                      required
                      options={BRAZILIAN_STATES}
                      value={selectedState}
                      onChange={(state) => setUf(state?.state ?? '')}
                      getOptionLabel={(state) => state.state}
                      isOptionEqualToValue={(state, value) =>
                        state.state === value.state
                      }
                    />
                  </S.StateField>
                </S.FieldsRow>

                {errorMessage && (
                  <S.ErrorMessage role="alert">{errorMessage}</S.ErrorMessage>
                )}

                <S.Actions>
                  <Button
                    type="submit"
                    backgroundColor={green[500]}
                    hoverBackgroundColor={green[600]}
                    color={gray[0]}
                    disabledBackgroundColor={green[500]}
                    disabledOpacity={0.48}
                    width="100%"
                    height="40px"
                    borderRadius="8px"
                    fontSize={fontSizes.sm}
                    disabled={!oabNumber || !uf}
                    loading={isSubmitting}
                  >
                    Confirmar OAB
                  </Button>
                </S.Actions>
              </S.Form>
            </>
          )}
        </S.Body>
      </S.Card>
    </ModalDialog>
  );
};

export default OabRegistrationModal;
